/**
 * Rich Embed — transforms fenced code blocks with special "languages"
 * into interactive embedded content (videos, maps, iframes, oEmbed, etc.).
 *
 * Works exactly like highlight.js but for rich content: Flexmark renders
 * ```youtube as <pre><code class="language-youtube">, and this module
 * replaces those blocks with the appropriate embed.
 *
 * ## Adding a new handler
 *
 * Import and register in the HANDLERS map below:
 *
 *   HANDLERS.set('myservice', {
 *       match: (content) => true,                     // optional filter
 *       render: (content, container) => container.innerHTML = '...',
 *       icon: 'fa-solid fa-cube',                     // optional icon
 *       label: 'My Service',                          // display name
 *   });
 *
 * The LLM writes:
 *   ```myservice
 *   https://myservice.com/item/123
 *   ```
 *
 * And the frontend renders it as rich content.
 */

import { logger } from '../../core/base.js';

// ── Handler registry ──────────────────────────────────────────────────

const HANDLERS = new Map();

/**
 * Register an embed handler.
 * @param {string|string[]} languages - language tag(s) to handle
 * @param {object} handler - { render(content, container), label?, icon?, match?(content) }
 */
export function registerEmbed(languages, handler) {
    const langs = Array.isArray(languages) ? languages : [languages];
    langs.forEach(lang => HANDLERS.set(lang, handler));
}

// ── Core processing ───────────────────────────────────────────────────

/**
 * Process all unprocessed embed blocks in the given root element.
 * Safe to call multiple times (idempotent via data-embed-processed).
 */
export function processEmbeds(root = document) {
    const blocks = root.querySelectorAll('pre > code[class*="language-"]');
    let count = 0;

    blocks.forEach(code => {
        const pre = code.parentElement;
        if (pre.dataset.embedProcessed) return;

        const lang = extractLanguage(code);
        if (!lang) return;

        const handler = HANDLERS.get(lang);
        if (!handler) return;

        const content = sanitizeContent(code.textContent);
        if (handler.match && !handler.match(content)) return;

        pre.dataset.embedProcessed = 'true';
        count++;

        const container = document.createElement('div');
        container.className = `ui-embed ui-embed--${lang}`;
        container.dataset.embedType = lang;

        try {
            handler.render(content, container);
            pre.replaceWith(container);
        } catch (err) {
            logger.warn(`[rich-embed] handler "${lang}" failed:`, err);
            // Leave the original code block on error
            pre.dataset.embedProcessed = 'error';
        }
    });

    if (count > 0) {
        logger.debug(`[rich-embed] processed ${count} embed(s)`);
    }
}

function extractLanguage(codeEl) {
    const match = codeEl.className.match(/language-(\S+)/);
    return match ? match[1] : null;
}

// ── Utility: responsive iframe wrapper ────────────────────────────────

export function createIframe(src, { aspectRatio = '16/9', allow = '', sandbox = '' } = {}) {
    const wrapper = document.createElement('div');
    wrapper.className = 'ui-embed-iframe';
    wrapper.style.aspectRatio = aspectRatio;

    const iframe = document.createElement('iframe');
    iframe.src = src;
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('loading', 'lazy');
    if (allow) iframe.setAttribute('allow', allow);
    if (sandbox) iframe.setAttribute('sandbox', sandbox);

    wrapper.appendChild(iframe);
    return wrapper;
}

// ── Utility: oEmbed resolver ──────────────────────────────────────────

const OEMBED_PROVIDERS = [
    { pattern: /youtube\.com\/watch|youtu\.be\//,           endpoint: 'https://www.youtube.com/oembed' },
    { pattern: /vimeo\.com\//,                              endpoint: 'https://vimeo.com/api/oembed.json' },
    { pattern: /twitter\.com\/|x\.com\//,                   endpoint: 'https://publish.twitter.com/oembed' },
    { pattern: /open\.spotify\.com\//,                      endpoint: 'https://open.spotify.com/oembed' },
    { pattern: /soundcloud\.com\//,                         endpoint: 'https://soundcloud.com/oembed' },
    { pattern: /codepen\.io\//,                             endpoint: 'https://codepen.io/api/oembed' },
    { pattern: /figma\.com\//,                              endpoint: 'https://www.figma.com/api/oembed' },
    { pattern: /dailymotion\.com\/video|dai\.ly\//,         endpoint: 'https://www.dailymotion.com/services/oembed' },
    { pattern: /slideshare\.net\//,                         endpoint: 'https://www.slideshare.net/api/oembed/2' },
    { pattern: /flickr\.com\/photos\//,                     endpoint: 'https://www.flickr.com/services/oembed/' },
    { pattern: /giphy\.com\/gifs\//,                        endpoint: 'https://giphy.com/services/oembed' },
    { pattern: /loom\.com\/share\//,                        endpoint: 'https://www.loom.com/v1/oembed' },
    { pattern: /tiktok\.com\//,                             endpoint: 'https://www.tiktok.com/oembed' },
];

export function findOembedEndpoint(url) {
    for (const { pattern, endpoint } of OEMBED_PROVIDERS) {
        if (pattern.test(url)) return endpoint;
    }
    return null;
}

export async function resolveOembed(url) {
    const endpoint = findOembedEndpoint(url);
    if (!endpoint) return null;

    try {
        const oembedUrl = `${endpoint}?url=${encodeURIComponent(url)}&format=json&maxwidth=800`;
        const resp = await fetch(oembedUrl);
        if (!resp.ok) return null;
        return await resp.json();
    } catch (err) {
        logger.warn('[rich-embed] oEmbed resolution failed:', err);
        return null;
    }
}

// ── Built-in handlers ─────────────────────────────────────────────────

// --- YouTube ---
registerEmbed(['youtube', 'yt'], {
    label: 'YouTube',
    icon: 'fa-brands fa-youtube',
    match: content => /youtube\.com|youtu\.be/.test(content) || /^[\w-]{10,12}$/.test(content.trim()),
    render(content, container) {
        const embedSrc = extractYoutubeEmbed(content);
        if (!embedSrc) { container.textContent = `Invalid YouTube URL: ${content}`; return; }
        container.appendChild(createIframe(embedSrc, {
            allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
        }));
    }
});

function extractYoutubeEmbed(input) {
    const trimmed = input.trim().split(/\s/)[0];

    // Playlist: ?list=XXX (no video ID needed)
    const listMatch = trimmed.match(/[?&]list=([\w-]+)/);
    if (listMatch && !trimmed.match(/[?&]v=/)) {
        return `https://www.youtube-nocookie.com/embed/videoseries?list=${listMatch[1]}`;
    }

    // Direct ID
    if (/^[\w-]{10,12}$/.test(trimmed)) {
        return `https://www.youtube-nocookie.com/embed/${trimmed}`;
    }

    // Video URL patterns
    const patterns = [
        /(?:youtube\.com\/watch\?.*?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]+)/,
        /youtube\.com\/(?:shorts|live)\/([\w-]+)/,
    ];
    for (const re of patterns) {
        const m = trimmed.match(re);
        if (m) {
            // If video is in a playlist, include the list param
            const src = `https://www.youtube-nocookie.com/embed/${m[1]}`;
            return listMatch ? `${src}?list=${listMatch[1]}` : src;
        }
    }
    return null;
}

// --- Vimeo ---
registerEmbed('vimeo', {
    label: 'Vimeo',
    icon: 'fa-brands fa-vimeo-v',
    match: content => /vimeo\.com\/\d+/.test(content) || /^\d+$/.test(content.trim().split(/\s/)[0]),
    render(content, container) {
        const trimmed = content.trim().split(/\s/)[0];
        const m = trimmed.match(/vimeo\.com\/(\d+)/) || trimmed.match(/^(\d+)$/);
        if (!m) { container.textContent = `Invalid Vimeo URL: ${content}`; return; }
        container.appendChild(createIframe(
            `https://player.vimeo.com/video/${m[1]}`,
            { allow: 'autoplay; fullscreen; picture-in-picture' }
        ));
    }
});

// --- Spotify ---
registerEmbed('spotify', {
    label: 'Spotify',
    icon: 'fa-brands fa-spotify',
    match: content => /open\.spotify\.com/.test(content),
    render(content, container) {
        // open.spotify.com/{track,album,playlist,episode,show}/xxx → embed url
        const originalUrl = content.trim().split(/\s/)[0];
        const embedUrl = originalUrl.replace(/open\.spotify\.com\/(?!embed)/, 'open.spotify.com/embed/');
        const isCompact = /\/track\/|\/episode\//.test(embedUrl);
        container.appendChild(createIframe(embedUrl, {
            aspectRatio: 'auto',
            allow: 'autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture'
        }));
        container.querySelector('.ui-embed-iframe').style.height = isCompact ? '152px' : '352px';
        // Fallback link in case the embed fails (hallucinated URL, removed content, etc.)
        const fallback = document.createElement('a');
        fallback.href = originalUrl;
        fallback.target = '_blank';
        fallback.rel = 'noopener';
        fallback.className = 'ui-embed-fallback';
        fallback.innerHTML = '<i class="fa-brands fa-spotify"></i> Ouvrir sur Spotify';
        container.appendChild(fallback);
    }
});

// --- SoundCloud ---
registerEmbed('soundcloud', {
    label: 'SoundCloud',
    icon: 'fa-brands fa-soundcloud',
    match: content => /soundcloud\.com/.test(content),
    async render(content, container) {
        container.innerHTML = '<div class="ui-embed-loading">Loading SoundCloud...</div>';
        const data = await resolveOembed(content.trim());
        if (data?.html) {
            container.innerHTML = sanitizeOembedHtml(data.html);
        } else {
            container.replaceChildren(buildFallbackLink(content.trim(), 'Open on SoundCloud'));
        }
    }
});

// --- Google Maps ---
registerEmbed(['map', 'maps', 'gmap'], {
    label: 'Google Maps',
    icon: 'fa-solid fa-map-location-dot',
    render(content, container) {
        const lines = content.trim().split('\n');
        const params = parseKeyValue(lines);
        const q = params.q || params.query || params.address || content.trim();
        const zoom = params.zoom || '13';

        // Use embed API (no key needed for simple search)
        const src = `https://maps.google.com/maps?q=${encodeURIComponent(q)}&z=${zoom}&output=embed`;
        container.appendChild(createIframe(src, { aspectRatio: '16/9' }));
    }
});

// --- OpenStreetMap ---
registerEmbed(['osm', 'openstreetmap'], {
    label: 'OpenStreetMap',
    icon: 'fa-solid fa-map',
    render(content, container) {
        const lines = content.trim().split('\n');
        const params = parseKeyValue(lines);
        const lat = params.lat || '48.8566';
        const lng = params.lng || params.lon || '2.3522';
        const zoom = params.zoom || '13';
        const src = `https://www.openstreetmap.org/export/embed.html?bbox=${lng-0.01},${lat-0.01},${parseFloat(lng)+0.01},${parseFloat(lat)+0.01}&layer=mapnik&marker=${lat},${lng}`;
        container.appendChild(createIframe(src, { aspectRatio: '16/9' }));
    }
});

// --- Generic iframe ---
registerEmbed('iframe', {
    label: 'Iframe',
    icon: 'fa-solid fa-window-maximize',
    render(content, container) {
        const lines = content.trim().split('\n');
        const params = parseKeyValue(lines);
        const src = params.src || params.url || lines[0].trim();
        const ratio = params.ratio || params['aspect-ratio'] || '16/9';
        const height = params.height;

        if (!src) { container.textContent = 'iframe: src or url required'; return; }

        const iframe = createIframe(src, {
            aspectRatio: height ? 'auto' : ratio,
            sandbox: params.sandbox || 'allow-scripts allow-same-origin allow-popups',
        });
        if (height) iframe.style.height = height;
        container.appendChild(iframe);
    }
});

// --- Generic oEmbed ---
registerEmbed(['embed', 'oembed'], {
    label: 'oEmbed',
    icon: 'fa-solid fa-link',
    async render(content, container) {
        const url = content.trim().split('\n')[0].trim();
        container.innerHTML = '<div class="ui-embed-loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</div>';

        const data = await resolveOembed(url);
        if (data?.html) {
            container.innerHTML = sanitizeOembedHtml(data.html);
            // Ensure iframes inside oEmbed are responsive
            const iframe = container.querySelector('iframe');
            if (iframe) {
                iframe.style.maxWidth = '100%';
                iframe.setAttribute('loading', 'lazy');
            }
        } else {
            // Fallback: try as iframe
            container.replaceChildren(createIframe(url, {
                sandbox: 'allow-scripts allow-same-origin allow-popups'
            }));
        }
    }
});

// --- Twitter / X ---
registerEmbed(['twitter', 'tweet', 'x'], {
    label: 'Twitter/X',
    icon: 'fa-brands fa-x-twitter',
    match: content => /twitter\.com|x\.com/.test(content),
    async render(content, container) {
        const url = content.trim().split('\n')[0].trim();
        container.innerHTML = '<div class="ui-embed-loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading tweet...</div>';
        const data = await resolveOembed(url);
        if (data?.html) {
            container.innerHTML = sanitizeOembedHtml(data.html);
            // Load Twitter widget JS if not already present
            if (!document.querySelector('script[src*="platform.twitter.com"]')) {
                const script = document.createElement('script');
                script.src = 'https://platform.twitter.com/widgets.js';
                script.async = true;
                document.head.appendChild(script);
            } else if (window.twttr?.widgets) {
                window.twttr.widgets.load(container);
            }
        } else {
            container.replaceChildren(buildFallbackLink(url, 'View on X', 'fa-brands fa-x-twitter'));
        }
    }
});

// --- CodePen ---
registerEmbed('codepen', {
    label: 'CodePen',
    icon: 'fa-brands fa-codepen',
    match: content => /codepen\.io/.test(content),
    render(content, container) {
        const url = content.trim().split(/\s/)[0];
        // Convert URL to embed format (skip if already /embed/)
        const embedUrl = url.includes('/embed/') ? url : url.replace('/pen/', '/embed/');
        const sep = embedUrl.includes('?') ? '&' : '?';
        container.appendChild(createIframe(embedUrl + sep + 'default-tab=result', { aspectRatio: '16/9' }));
    }
});

// --- Figma ---
registerEmbed('figma', {
    label: 'Figma',
    icon: 'fa-brands fa-figma',
    match: content => /figma\.com/.test(content),
    render(content, container) {
        const url = content.trim();
        const src = `https://www.figma.com/embed?embed_host=share&url=${encodeURIComponent(url)}`;
        container.appendChild(createIframe(src, { aspectRatio: '16/10' }));
    }
});

// --- Loom ---
registerEmbed('loom', {
    label: 'Loom',
    icon: 'fa-solid fa-video',
    match: content => /loom\.com\/share/.test(content),
    render(content, container) {
        const url = content.trim().replace('/share/', '/embed/');
        container.appendChild(createIframe(url, {
            aspectRatio: '16/9',
            allow: 'autoplay; fullscreen'
        }));
    }
});

// --- Dailymotion ---
registerEmbed(['dailymotion', 'dm'], {
    label: 'Dailymotion',
    icon: 'fa-solid fa-play',
    match: content => /dailymotion\.com|dai\.ly/.test(content),
    render(content, container) {
        const m = content.match(/(?:dailymotion\.com\/video\/|dai\.ly\/)([\w]+)/);
        if (!m) { container.textContent = `Invalid Dailymotion URL`; return; }
        container.appendChild(createIframe(
            `https://www.dailymotion.com/embed/video/${m[1]}`,
            { allow: 'autoplay; fullscreen' }
        ));
    }
});

// --- TikTok ---
registerEmbed('tiktok', {
    label: 'TikTok',
    icon: 'fa-brands fa-tiktok',
    match: content => /tiktok\.com/.test(content),
    async render(content, container) {
        const url = content.trim();
        container.innerHTML = '<div class="ui-embed-loading"><i class="fa-solid fa-spinner fa-spin"></i> Loading...</div>';
        const data = await resolveOembed(url);
        if (data?.html) {
            container.innerHTML = sanitizeOembedHtml(data.html);
        } else {
            container.replaceChildren(buildFallbackLink(url, 'View on TikTok', 'fa-brands fa-tiktok'));
        }
    }
});

// --- Mermaid diagrams ---
registerEmbed('mermaid', {
    label: 'Mermaid Diagram',
    icon: 'fa-solid fa-diagram-project',
    render(content, container) {
        container.className += ' ui-embed--mermaid';
        const pre = document.createElement('pre');
        pre.className = 'mermaid';
        pre.textContent = content;
        container.appendChild(pre);

        // Load mermaid.js on demand
        if (window.mermaid) {
            window.mermaid.run({ nodes: [pre] });
        } else if (!document.querySelector('script[src*="mermaid"]')) {
            const script = document.createElement('script');
            script.src = 'https://cdn.jsdelivr.net/npm/mermaid@11/dist/mermaid.min.js';
            script.onload = () => {
                const mermaidConfig = window.__richEmbedConfig?.mermaid || { startOnLoad: false, theme: 'dark' };
                window.mermaid.initialize(mermaidConfig);
                window.mermaid.run({ nodes: [pre] });
            };
            document.head.appendChild(script);
        }
    }
});

// --- PDF viewer ---
registerEmbed('pdf', {
    label: 'PDF',
    icon: 'fa-solid fa-file-pdf',
    render(content, container) {
        const url = content.trim();
        container.appendChild(createIframe(url, { aspectRatio: '3/4' }));
    }
});

// --- Image gallery (multiple URLs) ---
registerEmbed(['gallery', 'images'], {
    label: 'Gallery',
    icon: 'fa-solid fa-images',
    render(content, container) {
        const urls = content.trim().split('\n').filter(l => l.trim());
        const gallery = document.createElement('div');
        gallery.className = 'ui-embed-gallery';
        urls.forEach(url => {
            const link = document.createElement('a');
            link.href = url.trim();
            link.target = '_blank';
            link.rel = 'noopener';
            const img = document.createElement('img');
            img.src = url.trim();
            img.loading = 'lazy';
            img.alt = '';
            link.appendChild(img);
            gallery.appendChild(link);
        });
        container.appendChild(gallery);
    }
});

// --- Audio player ---
registerEmbed('audio', {
    label: 'Audio',
    icon: 'fa-solid fa-music',
    render(content, container) {
        const url = content.trim();
        const audio = document.createElement('audio');
        audio.controls = true;
        audio.preload = 'metadata';
        audio.src = url;
        audio.className = 'ui-embed-audio';
        container.appendChild(audio);
    }
});

// --- Video player (direct file) ---
registerEmbed('video', {
    label: 'Video',
    icon: 'fa-solid fa-film',
    render(content, container) {
        const lines = content.trim().split('\n');
        const params = parseKeyValue(lines);
        const src = params.src || params.url || lines[0].trim();

        const video = document.createElement('video');
        video.controls = true;
        video.preload = 'metadata';
        video.src = src;
        video.className = 'ui-embed-video';
        if (params.poster) video.poster = params.poster;
        container.appendChild(video);
    }
});

// ── Helpers ───────────────────────────────────────────────────────────

/**
 * Clean up LLM output quirks: stray backticks, surrounding quotes, trailing whitespace.
 */
function sanitizeContent(raw) {
    return raw
        .replace(/^[`'"]+|[`'"]+$/g, '')  // strip wrapping quotes/backticks
        .replace(/\r\n/g, '\n')            // normalize line endings
        .trim();
}

function parseKeyValue(lines) {
    const params = {};
    lines.forEach(line => {
        const m = line.match(/^(\w[\w-]*)\s*[:=]\s*(.+)$/);
        if (m) params[m[1].toLowerCase()] = m[2].trim();
    });
    return params;
}

// Hosts autorisés pour les iframe injectés via oEmbed. Doit rester aligné
// sur la directive `frame-src` de docker/nginx/conf.d/security-headers.inc.
const ALLOWED_OEMBED_IFRAME_HOSTS = new Set([
    'www.youtube.com', 'www.youtube-nocookie.com',
    'player.vimeo.com',
    'platform.twitter.com',
    'w.soundcloud.com',
    'www.tiktok.com',
    'www.dailymotion.com',
    'www.figma.com',
    'codepen.io',
    'open.spotify.com',
]);

function isAllowedIframeSrc(src) {
    if (!src) return false;
    try {
        const u = new URL(src, location.href);
        return u.protocol === 'https:' && ALLOWED_OEMBED_IFRAME_HOSTS.has(u.hostname);
    } catch {
        return false;
    }
}

/**
 * Sanitisation minimaliste du HTML retourné par les fournisseurs oEmbed
 * (Twitter, SoundCloud, TikTok…). Strip les balises actives, les iframe
 * vers des hosts non whitelistés, les attributs `on*` et les URIs
 * `javascript:`. Pas de dépendance externe : un fournisseur oEmbed
 * compromis ne doit pas pouvoir injecter de script.
 */
function sanitizeOembedHtml(html) {
    const tpl = document.createElement('template');
    tpl.innerHTML = html;
    const blockedTags = new Set(['script', 'style', 'object', 'embed', 'form', 'meta', 'link', 'base']);
    const toRemove = [];
    const walker = document.createTreeWalker(tpl.content, NodeFilter.SHOW_ELEMENT);
    let node;
    while ((node = walker.nextNode())) {
        const tag = node.tagName.toLowerCase();
        if (blockedTags.has(tag)) {
            toRemove.push(node);
            continue;
        }
        if (tag === 'iframe' && !isAllowedIframeSrc(node.getAttribute('src'))) {
            toRemove.push(node);
            continue;
        }
        for (const attr of [...node.attributes]) {
            const name = attr.name.toLowerCase();
            const value = attr.value;
            if (name.startsWith('on')) {
                node.removeAttribute(attr.name);
            } else if ((name === 'href' || name === 'src' || name === 'xlink:href') && /^\s*javascript:/i.test(value)) {
                node.removeAttribute(attr.name);
            }
        }
    }
    toRemove.forEach(n => n.remove());
    return tpl.innerHTML;
}

function buildFallbackLink(url, label, iconClass) {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener';
    a.className = 'ui-embed-fallback';
    if (iconClass) {
        const icon = document.createElement('i');
        icon.className = iconClass;
        a.appendChild(icon);
        a.appendChild(document.createTextNode(' '));
    }
    a.appendChild(document.createTextNode(label));
    return a;
}

// ── Auto-init ─────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => processEmbeds());

// Export for use by conversation.js after SSE refresh
export { HANDLERS };
